"use client";

import { motion, AnimatePresence } from "framer-motion";
import React, { useState, RefObject, useEffect } from "react";
import PostRetirementCorpusScreen, {
  POST_RETIREMENT_PLAN_CORPUS,
} from "../process-parts/PostRetirementCorpusScreen";
import PostRetirementDurationScreen from "../process-parts/PostRetirementDurationScreen";
import PostRetirementScenarioScreen from "../process-parts/PostRetirementScenarioScreen";
import SummaryPostRetirement from "../steps/SummaryPostRetirement";
import { PostRetirementFlowState, RetirementSummaryValues } from "@/type/financialPageTypes";
import { DEFAULT_RETIREMENT_DURATION } from "@/utils/financialPageFunctions";
import { PortfolioApiQuestion } from "@/type/servicesPageTypes";

type Screen = "corpus" | "duration" | "scenario" | "summary";
type Answer = {
  question_id: number;
  option_id: number;
  answer: string;
};

// Fallback ids used only if the API question list is ever empty.
const FALLBACK_AMOUNT_QUESTION_ID = 9201;
const FALLBACK_DURATION_QUESTION_ID = 9203;

const slideVariants = {
  enter: (dir: number) => ({ opacity: 0, x: dir * 24 }),
  center: { opacity: 1, x: 0 },
  exit: (dir: number) => ({ opacity: 0, x: dir * -24 }),
};

export default function PostRetirementFlow({
  currentStep,
  scrollContainerRef,
  questions,
  onAnswersChangeAction,
  onSummaryChangeAction,
  validateTrigger,
}: {
  currentStep: number;
  scrollContainerRef: RefObject<HTMLDivElement | null>;
  questions: PortfolioApiQuestion[];
  onAnswersChangeAction?: (answers: Record<number, Answer>) => void;
  onSummaryChangeAction?: (values: RetirementSummaryValues) => void;
  validateTrigger?: number;
}) {
  const SCREENS: Screen[] = ["corpus", "duration", "scenario", "summary"];
  const screen: Screen = SCREENS[currentStep - 1] ?? "corpus";

  // Step 1 (amount) and step 2 (currently "select SWP scheme", soon "select
  // age") are driven by the API; their question text and ids come from here
  // so a backend change to either question updates this screen automatically.
  const sortedQuestions = [...questions].sort((a, b) => (a.step ?? 0) - (b.step ?? 0));
  const amountQuestion = sortedQuestions[0];
  const durationQuestion = sortedQuestions[1];
  const amountQuestionId = amountQuestion?.id ?? FALLBACK_AMOUNT_QUESTION_ID;
  const durationQuestionId = durationQuestion?.id ?? FALLBACK_DURATION_QUESTION_ID;

  const [answers, setAnswers] = useState<Record<number, Answer>>({});

  useEffect(() => {
    onAnswersChangeAction?.(answers);
  }, [answers, onAnswersChangeAction]);

  const updateAnswer = (questionId: number, answer: string) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: { question_id: questionId, option_id: 0, answer },
    }));
  };

  const clearAnswer = (questionId: number) => {
    setAnswers((prev) => {
      const next = { ...prev };
      delete next[questionId];
      return next;
    });
  };

  const [state, setState] = useState<PostRetirementFlowState>({
    selectedPlan: null,
    customAmount: "",
    retirementDuration: DEFAULT_RETIREMENT_DURATION,
  });

  const corpus = state.selectedPlan
    ? POST_RETIREMENT_PLAN_CORPUS[state.selectedPlan]
    : Number(state.customAmount) || 0;

  // A chosen plan card and a typed custom amount both answer the same "amount"
  // question, so they're reconciled here instead of inside each handler
  // (letting the corpus screen clear one input when the other is used without
  // the two updates racing to overwrite the same answer).
  useEffect(() => {
    const amount = state.selectedPlan ? POST_RETIREMENT_PLAN_CORPUS[state.selectedPlan].toString() : state.customAmount;

    if (amount) {
      updateAnswer(amountQuestionId, amount);
    } else {
      clearAnswer(amountQuestionId);
    }
  }, [state.selectedPlan, state.customAmount, amountQuestionId]);

  useEffect(() => {
    updateAnswer(durationQuestionId, state.retirementDuration.toString());
  }, [state.retirementDuration, durationQuestionId]);

  return (
    <AnimatePresence mode="wait" custom={currentStep}>
      <motion.div
        key={screen}
        custom={currentStep}
        ref={scrollContainerRef}
        variants={slideVariants}
        initial="enter"
        animate="center"
        exit="exit"
        transition={{ duration: 0.22, ease: "easeInOut" }}
        className="relative h-full p-4 sm:p-6"
      >
        {screen === "corpus" && (
          <PostRetirementCorpusScreen
            stepTitle={amountQuestion?.question}
            selectedPlan={state.selectedPlan}
            onSelectPlanAction={(plan) => setState((s) => ({ ...s, selectedPlan: plan }))}
            customAmount={state.customAmount}
            onCustomAmountAction={(value) => setState((s) => ({ ...s, customAmount: value }))}
            validateTrigger={validateTrigger}
          />
        )}

        {screen === "duration" && (
          <PostRetirementDurationScreen
            stepTitle={durationQuestion?.question}
            retirementDuration={state.retirementDuration}
            onRetirementDurationAction={(years) => setState((s) => ({ ...s, retirementDuration: years }))}
            validateTrigger={validateTrigger}
          />
        )}

        {screen === "scenario" && (
          <PostRetirementScenarioScreen corpus={corpus} retirementDuration={state.retirementDuration} />
        )}

        {screen === "summary" && (
          <SummaryPostRetirement
            corpus={corpus}
            retirementDuration={state.retirementDuration}
            onSummaryChangeAction={onSummaryChangeAction}
          />
        )}
      </motion.div>
    </AnimatePresence>
  );
}
